/**
 * Карточка подборки: Card + превью, заголовок, прогресс. Стиль как в пазле (токены).
 */

import { createCard } from './Card';
import { soundManager } from '../../services/SoundManager';

export interface PackCardOptions {
  /** Элемент превью (например createLazyImage), будет помещён в rounded wrap */
  preview: HTMLElement | null;
  titleText: string;
  /** Для мини-прогрессбара и подписи "0/2" */
  progressCollected: number;
  progressTotal: number;
  onClick: () => void;
  /** Если false — показывается lockOverlay с текстом lockOverlayText */
  isPurchased?: boolean;
  lockOverlayText?: string;
  /** Опциональный бейдж (например "Ad"/"🎁") — показывается в углу превью */
  rewardBadge?: string;
}

/**
 * Создаёт интерактивную карточку подборки: превью (cover + rounded), заголовок, прогресс.
 * При !isPurchased — overlay с lockOverlayText. Press state как у кнопок в пазле.
 */
export function createPackCard(options: PackCardOptions): HTMLElement {
  const {
    preview,
    titleText,
    progressCollected,
    progressTotal,
    onClick,
    isPurchased = true,
    lockOverlayText = '1 пазл бесплатно',
    rewardBadge,
  } = options;

  const { card, inner } = createCard({ gapPx: 8 });
  card.classList.add('ui-pack-card');
  card.setAttribute('role', 'button');
  card.tabIndex = 0;

  const previewWrap = document.createElement('div');
  previewWrap.className = 'ui-pack-card-preview';
  previewWrap.style.cssText =
    'position:relative;width:100%;flex:1;min-height:0;overflow:hidden;border-radius:var(--ui-preview-radius, 12px);background:var(--ui-pill-background, #333)';
  if (preview) {
    preview.style.width = '100%';
    preview.style.height = '100%';
    preview.style.display = 'block';
    const img = preview.querySelector('img');
    if (img) img.style.objectFit = 'cover';
    previewWrap.appendChild(preview);
  }
  if (!isPurchased || rewardBadge) {
    const wrap = document.createElement('div');
    wrap.className = 'ui-pack-card-corner';
    wrap.style.cssText =
      'position:absolute;top:6px;right:6px;display:flex;align-items:center;gap:4px;pointer-events:none';
    if (!isPurchased) {
      const lockCapsule = document.createElement('span');
      lockCapsule.className = 'ui-pack-card-lock-indicator';
      lockCapsule.setAttribute('aria-hidden', 'true');
      lockCapsule.textContent = '\u{1F512}';
      wrap.appendChild(lockCapsule);
    }
    if (rewardBadge) {
      const badge = document.createElement('span');
      badge.className = 'ui-pack-card-badge';
      badge.textContent = rewardBadge;
      wrap.appendChild(badge);
    }
    previewWrap.appendChild(wrap);
  }
  if (!isPurchased && lockOverlayText) {
    const lockPill = document.createElement('div');
    lockPill.className = 'ui-pack-card-lock-pill';
    lockPill.textContent = lockOverlayText;
    previewWrap.appendChild(lockPill);
  }
  inner.appendChild(previewWrap);

  const titleEl = document.createElement('span');
  titleEl.className = 'ui-pack-card-title';
  titleEl.textContent = titleText;
  inner.appendChild(titleEl);

  const progressRow = document.createElement('div');
  progressRow.className = 'ui-pack-card-progress-row';
  const barTrack = document.createElement('div');
  barTrack.className = 'ui-pack-card-progress-track';
  const barFill = document.createElement('div');
  barFill.className = 'ui-pack-card-progress-fill';
  const pct = progressTotal > 0 ? progressCollected / progressTotal : 0;
  barFill.style.width = `${Math.min(1, pct) * 100}%`;
  barTrack.appendChild(barFill);
  progressRow.appendChild(barTrack);
  const progressLabel = document.createElement('span');
  progressLabel.className = 'ui-pack-card-progress';
  progressLabel.textContent = `${progressCollected}/${progressTotal}`;
  progressRow.appendChild(progressLabel);
  inner.appendChild(progressRow);

  const setPressed = (pressed: boolean) => {
    card.classList.toggle('ui-pack-card--pressed', pressed);
  };
  card.addEventListener('click', () => {
    soundManager.playClick();
    onClick();
  });
  card.addEventListener('mousedown', () => setPressed(true));
  card.addEventListener('mouseup', () => setPressed(false));
  card.addEventListener('mouseleave', () => setPressed(false));
  card.addEventListener('touchstart', () => setPressed(true), { passive: true });
  card.addEventListener('touchend', () => setPressed(false), { passive: true });

  return card;
}
